import React from 'react';
import {
  Card, CardContent, CardHeader, Typography, Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePlacesWidget } from 'react-google-autocomplete';
import { TALENTS_BASIC_INPUTS_NAME } from '../../Constants';
import { styleTalentDetails } from '../../styles';
import STRING_KEYS from '../../../../language/keys';
import { checkErrorMessage, getPlaceBasedGoogle } from '../../utils';
import { CONFIG } from '../../../../config';
import ErrorMessage from '../../../../components/ErrorMessage';
import { Anchor } from '../../../../components/Anchor';
import TableBasicEditRow from './TalentBasicRow';

type TalentBasicDetailsProps = {
  formErrors?: ErrorsValidatationDefauls,
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
  firstName: string,
  lastName: string,
  city: string,
  state: string,
  country: string,
  onLocationChanged?: (item:LocationType) => void,
  disabled?: boolean,
  viewMode: ViewMode
};

const TalentBasicDetails = (
  {
    formErrors, onInputChange,
    firstName,
    lastName,
    city,
    state,
    country,
    onLocationChanged,
    disabled,
    viewMode,
  }
    :TalentBasicDetailsProps)
: JSX.Element => {
  const { t: translate } = useTranslation();
  const { ref: autocompleteRef } = usePlacesWidget({
    apiKey: CONFIG.GOOGLE_API_KEY,
    onPlaceSelected: (place: google.maps.places.PlaceResult) => {
      if (place.address_components) {
        const {
          city: foundCity,
          state: foundState,
          country: foundCountry,
        } = getPlaceBasedGoogle(place.address_components, place.geometry);
        if (onLocationChanged) {
          onLocationChanged({
            city: foundCity || '',
            state: foundState || '',
            country: foundCountry || '',
          });
        }
      }
    },
  });

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onInputChange) {
      onInputChange(e);
    }
  };

  return (
    <Card style={ {
      marginTop: '32px',
      position: 'relative',
      backgroundColor: disabled ? 'rgb(243, 244, 246)' :
        'white',
      pointerEvents: disabled ? 'none' :
        'visible',
    } }
    >
      <Anchor id="basic-details" />
      <CardHeader title={ (
        <Typography variant="h6">
          { translate(STRING_KEYS.TALENTS_EDIT_BASIC_DETAILS_TITLE) }
        </Typography>
      ) }
      />
      <Divider />
      <CardContent style={ styleTalentDetails.cardContent.form.CardContent.removePB }
        sx={ styleTalentDetails.cardContent.form.CardContent.sx }
      >
        <TableBasicEditRow
          error={ checkErrorMessage(formErrors?.firstname) }
          value={ firstName }
          onInputChange={ handleInput }
          name={ TALENTS_BASIC_INPUTS_NAME.FIRST_NAME }
          label={ translate(STRING_KEYS.TALENTS_EDIT_BASIC_DETAILS_INPUTS_LABEL_FIRSTNAME) }
          viewMode={ viewMode }
        />
        { formErrors?.firstname ? <ErrorMessage message={ formErrors?.firstname } /> : <Divider /> }

        <TableBasicEditRow
          name={ TALENTS_BASIC_INPUTS_NAME.LAST_NAME }
          error={ checkErrorMessage(formErrors?.lastname) }
          value={ lastName }
          onInputChange={ handleInput }
          label={ translate(STRING_KEYS.TALENTS_EDIT_BASIC_DETAILS_INPUTS_LABEL_LASTNAME) }
          viewMode={ viewMode }
        />

        { formErrors?.lastname ? <ErrorMessage message={ formErrors?.lastname } /> : <Divider /> }

        <TableBasicEditRow
          name={ TALENTS_BASIC_INPUTS_NAME.CITY }
          ref={ autocompleteRef }
          error={ checkErrorMessage(formErrors?.city) }
          value={ city }
          onInputChange={ handleInput }
          label={ translate(STRING_KEYS.TALENTS_EDIT_BASIC_DETAILS_INPUTS_LABEL_CITY) }
          viewMode={ viewMode }
        />

        { formErrors?.city ? <ErrorMessage message={ formErrors?.city } /> : <Divider /> }

        <TableBasicEditRow
          name={ TALENTS_BASIC_INPUTS_NAME.STATE }
          error={ checkErrorMessage(formErrors?.state) }
          value={ state }
          onInputChange={ handleInput }
          label={ translate(STRING_KEYS.TALENTS_EDIT_BASIC_DETAILS_INPUTS_LABEL_STATE) }
          viewMode={ viewMode }
        />

        { formErrors?.state ? <ErrorMessage message={ formErrors?.state } /> : <Divider /> }
        <TableBasicEditRow
          name={ TALENTS_BASIC_INPUTS_NAME.COUNTRY }
          error={ checkErrorMessage(formErrors?.country) }
          value={ country }
          onInputChange={ handleInput }
          label={ translate(STRING_KEYS.TALENTS_EDIT_BASIC_DETAILS_INPUTS_LABEL_COUNTRY) }
          viewMode={ viewMode }
        />

        { formErrors?.country ? <ErrorMessage message={ formErrors?.country } /> : null }
      </CardContent>
    </Card>
  );
};

export default React.memo(TalentBasicDetails);

TalentBasicDetails.defaultProps = {
  formErrors: {},
  onInputChange: null,
  onLocationChanged: null,
  disabled: false,
};
